import { DialogDisclosure, Heading } from "@ariakit/react";
import { DialogTrigger, MiddotSeparated, Success } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { Link } from "wouter";
import { EmptyState } from "../EmptyState/EmptyState.tsx";
import { BuildMode } from "../fmt.ts";
import { ShareGangDialog } from "../GangFormDialog/ShareGangDialog.tsx";
import {
  ALaCarteOnboardingView,
  CampaignModeOnboardingView,
  ChaosModeOnboardingView,
  ChefsRecOnboardingView,
  OnboardingGuard,
} from "../Onboarding/OnboardingCard.tsx";
import { CheatModeNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { ModifyStashDialog } from "../StepperDialog/StepperDialog.tsx";
import { Cap } from "./Cap.tsx";
import { ChaosModeView } from "./ChaosModeView.tsx";
import { ChefsRecView } from "./ChefsRecView.tsx";
import { ChooseFactionDialog } from "./ChooseFactionDialog.tsx";
import { GangActions } from "./GangActions.tsx";
import { GangModelsList } from "./GangModelsList.tsx";
import * as css from "./style.css.ts";
import { useGangContext } from "./useGang.ts";

function FactionPicker() {
  const { readOnly, gang } = useGangContext();
  const lookup = gang.faction ?? new Success(null);

  if (lookup.isSuccess) {
    const faction = lookup.value;

    if (!readOnly) {
      return (
        <DialogTrigger>
          <ChooseFactionDialog gang={gang} />
          <DialogDisclosure className={interactiveText}>
            {faction?.name ?? "Choose faction"}
          </DialogDisclosure>
        </DialogTrigger>
      );
    }

    return <>{faction?.name ?? "No faction"}</>;
  }

  return <>Faction unknown (id: {lookup.failure.missingKey})</>;
}

export function GangHeader() {
  const { gang } = useGangContext();
  const { mode } = gang;

  return (
    <div>
      <div className={css.gang.header}>
        <div className={css.gang.headerContent}>
          <div className={css.gang.mode}>{BuildMode.get(gang.mode.type)}</div>
          <Heading className={css.gang.heading}>{gang.name}</Heading>

          <div className={css.gang.subheading}>
            <MiddotSeparated>
              <FactionPicker />

              {`Reputation: ${gang.reputation}`}
            </MiddotSeparated>
          </div>
        </div>

        {mode.isManualBuildMode ? (
          <Cap
            cost={gang.ruleset.constants.aLaCarteCapsBudget - gang.cost}
            label="Stash"
            additionalInfo="caps"
          />
        ) : gang.stash !== null ? (
          <DialogTrigger>
            <ModifyStashDialog />

            <DialogDisclosure className={css.stashButton.container}>
              <Cap
                cost={gang.stash}
                label="Stash"
                additionalInfo="caps"
                tilt="none"
              />

              <div className={css.stashButton.indicator}>+</div>
            </DialogDisclosure>
          </DialogTrigger>
        ) : null}
      </div>

      <div className={css.gang.headerActions}>
        <Link href={`~/print/g/${gang.id}`} className={css.gang.headerButton}>
          Print
        </Link>

        <DialogTrigger>
          <ShareGangDialog />
          <DialogDisclosure className={css.gang.headerButton}>
            Share
          </DialogDisclosure>
        </DialogTrigger>
      </div>

      {gang.mode.isCheatModeActive && (
        <div className={css.gang.cheatMode}>
          <CheatModeNote />
        </div>
      )}

      {gang.description && (
        <div className={css.gang.description}>{gang.description}</div>
      )}
    </div>
  );
}

function DefaultView() {
  const { gang } = useGangContext();
  const { models } = gang;

  return (
    <div className={css.gang.container}>
      <GangHeader />

      {models.length === 0 ? (
        <EmptyState image="dumb" message="Gather your gits!" />
      ) : (
        <GangModelsList />
      )}

      <GangActions />
    </div>
  );
}

export function GangView() {
  const { gang, readOnly } = useGangContext();

  if (readOnly) {
    return <DefaultView />;
  }

  switch (gang.mode.type) {
    case "CHEFS_REC": {
      return (
        <OnboardingGuard
          topic="chefsRec"
          component={<ChefsRecOnboardingView />}
        >
          <ChefsRecView />
        </OnboardingGuard>
      );
    }

    case "CHAOS_MODE": {
      return (
        <OnboardingGuard
          topic="chaosMode"
          component={<ChaosModeOnboardingView />}
        >
          <ChaosModeView />
        </OnboardingGuard>
      );
    }

    case "A_LA_CARTE": {
      return (
        <OnboardingGuard
          topic="aLaCarte"
          component={<ALaCarteOnboardingView />}
        >
          <DefaultView />
        </OnboardingGuard>
      );
    }

    case "CAMPAIGN": {
      return (
        <OnboardingGuard
          topic="campaign"
          component={<CampaignModeOnboardingView />}
        >
          <DefaultView />
        </OnboardingGuard>
      );
    }

    default: {
      return <DefaultView />;
    }
  }
}
